import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('theme')
    return (saved as Theme) || 'light'
  })

  useEffect(() => {
    localStorage.setItem('theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
    
    // Update PrimeReact theme
    const themeLink = document.getElementById('theme-link') as HTMLLinkElement
    if (themeLink) {
      themeLink.href = `https://cdn.jsdelivr.net/npm/primereact@10.9.5/resources/themes/lara-${theme}-blue/theme.css`
    } else {
      const link = document.createElement('link')
      link.id = 'theme-link'
      link.rel = 'stylesheet'
      link.href = `https://cdn.jsdelivr.net/npm/primereact@10.9.5/resources/themes/lara-${theme}-blue/theme.css`
      document.head.appendChild(link)
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
